import { twJoin } from "tailwind-merge"
import { useAudioRecorder } from 'react-audio-voice-recorder';
import { useState, useEffect, useRef } from "react";
import Button from "./Button";

const MAX_RECORDING_SECONDS = 15 * 60; // 15 minutes
const WARNING_SECONDS = 60; // warn when 1 minute remains
const WAVEFORM_BARS = 32;

function formatTime(seconds) {
    const m = Math.floor(seconds / 60).toString().padStart(2, '0');
    const s = (seconds % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
}

function Recorder({onComplete, onReset, isUploading}) {
    const [isPlaying, setIsPlaying] = useState(false);
    const [playbackTime, setPlaybackTime] = useState(0);
    const [playbackDuration, setPlaybackDuration] = useState(0);
    const [capturedDuration, setCapturedDuration] = useState(0);
    const audioRef = useRef(null);
    const {
        startRecording,
        stopRecording,
        recordingBlob,
        isRecording,
        recordingTime,
    } = useAudioRecorder();

    useEffect(() => {
        return () => {
            if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current = null;
            }
        };
    }, []);

    const canvasRef = useRef(null);
    const audioContextRef = useRef(null);
    const analyserRef = useRef(null);
    const animationRef = useRef(null);
    const streamRef = useRef(null);

    useEffect(() => {
        if (isRecording) {
            startVisualization();
        } else {
            stopVisualization();
        }
        return () => stopVisualization();
    }, [isRecording]);

    const startVisualization = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
            streamRef.current = stream;

            const AudioCtx = window.AudioContext || window.webkitAudioContext;
            const audioContext = new AudioCtx();
            audioContextRef.current = audioContext;

            const analyser = audioContext.createAnalyser();
            analyser.fftSize = 64; // 32 frequency bins
            analyserRef.current = analyser;

            const source = audioContext.createMediaStreamSource(stream);
            source.connect(analyser);

            const dataArray = new Uint8Array(analyser.frequencyBinCount);

            const draw = () => {
                animationRef.current = requestAnimationFrame(draw);
                const canvas = canvasRef.current;
                if (!canvas) return;

                analyser.getByteFrequencyData(dataArray);

                const ctx = canvas.getContext('2d');
                ctx.clearRect(0, 0, canvas.width, canvas.height);

                const barWidth = Math.floor((canvas.width / WAVEFORM_BARS) - 2);

                for (let i = 0; i < WAVEFORM_BARS; i++) {
                    const amplitude = dataArray[i] / 255;
                    const barHeight = Math.max(3, amplitude * canvas.height);
                    const x = i * (barWidth + 2);
                    const y = (canvas.height - barHeight) / 2;
                    ctx.fillStyle = `rgba(87, 201, 232, ${0.35 + amplitude * 0.65})`;
                    ctx.fillRect(x, y, barWidth, barHeight);
                }
            };

            draw();
        } catch {
            // Visualization unavailable — recording still works fine
        }
    };

    const stopVisualization = () => {
        if (animationRef.current) {
            cancelAnimationFrame(animationRef.current);
            animationRef.current = null;
        }
        if (audioContextRef.current) {
            audioContextRef.current.close();
            audioContextRef.current = null;
        }
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(t => t.stop());
            streamRef.current = null;
        }
        // Clear canvas
        const canvas = canvasRef.current;
        if (canvas) {
            canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height);
        }
    };

    useEffect(() => {
        if (isRecording) {
            if (recordingTime >= MAX_RECORDING_SECONDS) {
                stopRecording();
            } else {
                setCapturedDuration(recordingTime);
            }
        }
    }, [recordingTime, isRecording]);

    const remaining = MAX_RECORDING_SECONDS - (recordingTime || 0);
    const isNearLimit = isRecording && remaining <= WARNING_SECONDS;

    const play = () => {
        setIsPlaying(true);
        setPlaybackTime(0);
        const blobUrl = URL.createObjectURL(recordingBlob);
        const audio = new Audio(blobUrl);
        audioRef.current = audio;
        audio.onloadedmetadata = () => setPlaybackDuration(audio.duration);
        audio.ontimeupdate = () => setPlaybackTime(audio.currentTime);
        audio.onended = () => {
            setIsPlaying(false);
            URL.revokeObjectURL(blobUrl);
        };
        audio.play();
    };

    const stopPlayback = () => {
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
            audioRef.current = null;
        }
        setIsPlaying(false);
        setPlaybackTime(0);
    };

    let circleIcon = "fa-microphone-alt";
    let circleLabel = "Record";
    let circleOnClick = startRecording;

    if (isRecording) {
        circleIcon = "fa-stop";
        circleLabel = "Stop";
        circleOnClick = stopRecording;
    } else if (isPlaying) {
        circleIcon = "fa-stop";
        circleLabel = "Stop";
        circleOnClick = stopPlayback;
    } else if (recordingBlob) {
        circleIcon = "fa-rotate-right";
        circleLabel = "Replay";
        circleOnClick = play;
    }

    return (
        <div>
            {isUploading ? (
                <div className="mx-auto w-[300px] h-[300px] bg-blessings rounded-full flex items-center justify-center">
                    <div className="text-center">
                        <i className="fas fa-spinner fa-spin text-8xl text-white mb-3"></i>
                        <div className="text-white text-2xl">Uploading...</div>
                    </div>
                </div>
            ) : (
                <div className="mx-auto w-[300px] h-[300px] bg-blessings rounded-full flex items-center justify-center cursor-pointer" onClick={circleOnClick}>
                    <div className="text-center">
                        <i className={twJoin("fas text-8xl text-white mb-3", circleIcon)}></i>
                        <div className="text-white text-2xl">{circleLabel}</div>
                    </div>
                </div>
            )}

            <canvas
                ref={canvasRef}
                width={300}
                height={48}
                className={twJoin(
                    "mx-auto block mt-4 rounded",
                    isRecording ? "opacity-100" : "opacity-0"
                )}
            />

            {(isRecording || recordingBlob) && !isUploading ? (
                <div className="mt-2 text-center">
                    {isRecording ? (
                        <>
                            <div className="text-2xl font-mono font-bold text-blessings">
                                {formatTime(recordingTime || 0)}
                            </div>
                            <div className={twJoin("text-sm mt-1", isNearLimit ? "text-red-500 font-bold" : "text-gray-400")}>
                                {formatTime(remaining)} remaining
                            </div>
                        </>
                    ) : recordingBlob ? (
                        <div className="text-2xl font-mono font-bold text-blessings">
                            {formatTime(Math.floor(playbackTime))}
                            <span className="text-gray-400 text-base font-normal">
                                {" / "}{formatTime(Math.floor(playbackDuration || capturedDuration))}
                            </span>
                        </div>
                    ) : null}
                </div>
            ) : null}

            {recordingBlob && !isUploading ?
                <div className="flex gap-4 mt-8 justify-between">
                    <Button bgColor="bg-blessings hover:bg-blessings/90" onClick={() => onReset()}>Restart</Button>
                    <Button bgColor="bg-blessings hover:bg-blessings/90" onClick={() => onComplete(recordingBlob)}>Publish</Button>
                </div>
                : null}
        </div>
    )
}

export default Recorder
